import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  useGetProfileQuery,
  useGetBalanceQuery,
  useGetDashboardStatsQuery,
  useCreateWithdrawalMutation,
} from "../store/api";
import { useLocalStorage } from "../hooks/useLocalStorage";

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px);
  padding: 2rem;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1.5rem;
    min-height: calc(100vh - 100px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  max-width: 1200px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: transparent;
  padding: 2rem 1.5rem;
  border-right: 1px solid var(--border-divider);
  border-bottom: 1px solid var(--border-divider);
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:nth-child(2n) {
    border-right: none;
  }

  &:nth-last-child(-n + 2) {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-card-hover);
  }

  @media (max-width: 768px) {
    border-right: none;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 600;
  line-height: 1.4;
  flex: 1;
`;

const StatChange = styled.div.withConfig({
  shouldForwardProp: (prop) => !["positive"].includes(prop),
})<{ positive: boolean }>`
  font-size: 0.75rem;
  color: ${(props) => (props.positive ? "#194BFF" : "#ef4444")};
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  background: ${(props) =>
    props.positive
      ? "linear-gradient(135deg, rgba(25, 75, 255, 0.1) 0%, rgba(57, 112, 255, 0.1) 100%)"
      : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)"};
  border-radius: 0.5rem;
  border: 1px solid
    ${(props) =>
      props.positive ? "rgba(25, 75, 255, 0.2)" : "rgba(239, 68, 68, 0.2)"};
  display: inline-block;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) =>
      props.positive
        ? "linear-gradient(135deg, rgba(25, 75, 255, 0.15) 0%, rgba(57, 112, 255, 0.15) 100%)"
        : "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)"};
    transform: translateY(-1px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !["disabled", "variant"].includes(prop),
})<{
  disabled?: boolean;
  variant?: "primary" | "secondary";
}>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  ${({ disabled, variant = "primary" }) => {
    if (disabled) {
      return `
        background: var(--bg-input-disabled);
        color: var(--text-disabled);
        border: 1px solid var(--border-color);
      `;
    }

    if (variant === "secondary") {
      return `
        background: transparent;
        color: var(--accent-primary);
        border: 1px solid var(--accent-primary);
        &:hover {
          background: var(--accent-primary);
          color: white;
          transform: translateY(-1px);
        }
      `;
    }

    return `
      background: var(--accent-primary);
      color: white;
      &:hover {
        background: var(--accent-primary-hover);
        transform: translateY(-1px);
      }
    `;
  }}
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const OrderMetric = styled.div.withConfig({
  shouldForwardProp: (prop) => !["variant"].includes(prop),
})<{ variant?: "total" | "success" | "failed" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: var(--bg-card-hover);
  border-radius: 0.75rem;
  border: 1px solid var(--border-light);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
    background: var(--bg-hover);
  }
`;

const OrderMetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
`;

const OrderMetricLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
  text-align: center;
  margin-top: 0.25rem;
`;

// Модальное окно
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-modal);
  border-radius: 0.75rem;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--border-color);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);

  &:hover {
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-input);

  &:focus {
    outline: none;
    border-color: var(--border-input-focus);
    box-shadow: var(--focus-ring);
  }

  &:invalid {
    border-color: var(--accent-error);
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !["variant"].includes(prop),
})<{ variant?: "primary" | "secondary" }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${({ variant }) =>
    variant === "primary"
      ? `
    background: var(--accent-primary);
    color: white;
    border: none;
    
    &:hover {
      background: var(--accent-primary-hover);
    }
  `
      : `
    background: var(--bg-card);
    color: var(--text-primary);
    border: 1px solid var(--border-input);
    
    &:hover {
      background: var(--bg-hover);
    }
  `}

  &:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: var(--accent-error);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessText = styled.div`
  color: var(--accent-primary);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const HelperText = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
`;

// Селектор периодов
const PeriodSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: transparent;
  max-width: 1200px;
  width: 100%;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: 0.75rem 1rem;
    gap: 0.75rem;
  }
`;

const PeriodButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !["isActive"].includes(prop),
})<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${(props) => (props.isActive ? "var(--accent-primary)" : "var(--border-divider)")};
  border-radius: 0.5rem;
  background: ${(props) => (props.isActive ? "var(--accent-primary)" : "transparent")};
  color: ${(props) => (props.isActive ? "#ffffff" : "var(--text-primary)")};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${(props) =>
      props.isActive ? "var(--accent-primary-hover)" : "var(--bg-card-hover)"};
    border-color: ${(props) => (props.isActive ? "var(--accent-primary-hover)" : "var(--border-hover)")};
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const CustomPeriodContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isVisible"].includes(prop),
})<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--bg-hover);
  border-radius: 0.5rem;
  border: 1px solid var(--border-divider);

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const DateInput = styled.input`
  background: var(--bg-input);
  border: 1px solid var(--border-divider);
  border-radius: 0.375rem;
  padding: 0.375rem 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 120px;

  &:focus {
    outline: none;
    border-color: var(--border-input-focus);
    box-shadow: var(--focus-ring);
  }

  @media (max-width: 768px) {
    width: 110px;
    font-size: 0.8125rem;
  }
`;

const DateSeparator = styled.span`
  color: var(--text-muted);
  font-weight: 500;
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--accent-error);
  border-radius: 0.5rem;
  background: var(--bg-card);
  color: var(--accent-error);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
  font-weight: 600;
  margin-left: 0.5rem;
  opacity: 0;
  animation: fadeIn 0.3s ease-out forwards;

  &:hover {
    background: var(--accent-error-light);
    color: #dc2626;
    border-color: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(239, 68, 68, 0.2);
  }

  &:active {
    transform: translateY(0) scale(0.95);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.875rem;
    margin-left: 0.375rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 1rem;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
    border-color: var(--border-hover);
  }

  &:active {
    transform: translateY(1px);
  }
`;

type PeriodType = "24h" | "7d" | "30d" | "all" | "custom";

function Dashboard() {
  const navigate = useNavigate();
  const {
    data: profile,
    error: profileError,
    isLoading: profileLoading,
  } = useGetProfileQuery();
  const {
    data: balance,
    error: balanceError,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useGetBalanceQuery();

  // Состояние для выбора периода с сохранением в localStorage
  const [selectedPeriod, setSelectedPeriod] = useLocalStorage<PeriodType>(
    "dashboard-selected-period",
    "30d"
  );
  const [customDateFrom, setCustomDateFrom] = useLocalStorage<string>(
    "dashboard-custom-date-from",
    ""
  );
  const [customDateTo, setCustomDateTo] = useLocalStorage<string>(
    "dashboard-custom-date-to",
    ""
  );

  const [createWithdrawal, { isLoading: isCreating }] =
    useCreateWithdrawalMutation();

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    walletAddress: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const {
    data: statsData,
    error: statsError,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useGetDashboardStatsQuery({
    period: selectedPeriod === "custom" ? undefined : selectedPeriod,
  });

  const handleRefresh = () => {
    refetchStats();
    refetchBalance();
  };

  // Автообновление при монтировании компонента (при клике в сайдбаре)
  useEffect(() => {
    refetchStats();
    refetchBalance();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Отладочная информация
  console.log("Dashboard Debug:", {
    profile,
    profileError,
    profileLoading,
    balance,
    balanceError,
    balanceLoading,
    statsData,
    statsError,
    statsLoading,
  });

  // Вычисляем статистику из реальных данных
  const totalOrders = statsData?.totalOrders || 0;
  const completedOrders = statsData?.completedOrders || 0;
  const failedOrders = statsData?.failedOrders || 0;
  const cancelledOrders = statsData?.cancelledOrders || 0;
  const processedVolumeRub = statsData?.processedVolumeRub || 0;
  const netProfit = statsData?.netProfit || 0;
  const processedVolumeUsdt = statsData?.processedVolume || 0;

  // Форматирование суммы
  const formatMoney = (amount: number | undefined | null, currency: string = "USDT"): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `0.00 ${currency}`;
    }
    return `${amount.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  const merchant = profile?.merchant;
  const currentBalance = balance?.balance || 0;
  const frozenBalance = balance?.frozenBalance || 0;
  const availableBalance = Math.max(0, currentBalance - frozenBalance);

  // Обработчики событий для периодов
  const handlePeriodChange = (period: PeriodType) => {
    console.log("🔄 Changing period to:", period);
    setSelectedPeriod(period);
    if (period !== "custom") {
      setCustomDateFrom("");
      setCustomDateTo("");
    }
  };

  const handleCustomDateChange = (type: "from" | "to", value: string) => {
    if (type === "from") {
      setCustomDateFrom(value);
    } else {
      setCustomDateTo(value);
    }
  };

  // Функция для сброса фильтров к дефолтному состоянию
  const handleResetFilters = () => {
    setSelectedPeriod("30d"); // Возвращаем к дефолтному периоду
    setCustomDateFrom("");
    setCustomDateTo("");
  };

  // Проверяем, нужно ли показывать кнопку сброса
  const shouldShowResetButton =
    selectedPeriod !== "30d" || customDateFrom !== "" || customDateTo !== "";

  // Если есть ошибки загрузки, показываем их
  if (profileError || balanceError || statsError) {
    return (
      <DashboardContainer>
        <div style={{ color: "#ef4444", textAlign: "center" }}>
          <h2>Ошибка загрузки данных</h2>
          <p>Profile Error: {JSON.stringify(profileError)}</p>
          <p>Balance Error: {JSON.stringify(balanceError)}</p>
          <p>Stats Error: {JSON.stringify(statsError)}</p>
        </div>
      </DashboardContainer>
    );
  }

  // Если данные загружаются, показываем загрузку
  if (profileLoading || balanceLoading || statsLoading) {
    return (
      <DashboardContainer>
        <div style={{ color: "#64748b", textAlign: "center" }}>
          <h2>Загрузка...</h2>
        </div>
      </DashboardContainer>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setWithdrawForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!merchant?.id) {
      setError("Информация о мерчанте не найдена");
      return;
    }

    const amount = parseFloat(withdrawForm.amount);
    if (!amount || amount <= 0) {
      setError("Введите корректную сумму");
      return;
    }

    if (amount > availableBalance) {
      setError("Недостаточно средств на балансе");
      return;
    }

    if (!withdrawForm.walletAddress.match(/^T[A-Za-z1-9]{33}$/)) {
      setError(
        "Введите корректный TRC-20 адрес (должен начинаться с T и содержать 34 символа)"
      );
      return;
    }

    try {
      await createWithdrawal({
        amount: amount,
        currency: "USDT",
        walletAddress: withdrawForm.walletAddress,
      }).unwrap();

      setSuccess("Заявка на вывод успешно создана!");
      setWithdrawForm({
        amount: "",
        walletAddress: "",
      });
      setTimeout(() => {
        setShowWithdrawModal(false);
        setSuccess("");
      }, 2000);
    } catch (err: any) {
      setError(err?.data?.message || "Ошибка при создании заявки на вывод");
    }
  };

  return (
    <DashboardContainer>
      <Header>
        <RefreshButton onClick={handleRefresh}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          Обновить
        </RefreshButton>
      </Header>
      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatLabel>Доступный баланс</StatLabel>
            <ButtonGroup>
              <ActionButton onClick={() => navigate("/balance")}>
                Вывести
              </ActionButton>
            </ButtonGroup>
          </StatHeader>
          <StatContent>
            <StatValue>{formatMoney(availableBalance)}</StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                lineHeight: 1.4,
              }}
            >
              В апелляциях: {formatMoney(balance?.disputeBalance || 0)}
              <br />
              В обработке: {formatMoney(balance?.frozenBalance || 0)}
            </div>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Статистика ордеров</StatLabel>
          </StatHeader>
          <StatContent>
            <OrdersGrid>
              <OrderMetric>
                <OrderMetricValue>{totalOrders}</OrderMetricValue>
                <OrderMetricLabel>Всего</OrderMetricLabel>
              </OrderMetric>
              <OrderMetric>
                <OrderMetricValue>{completedOrders}</OrderMetricValue>
                <OrderMetricLabel>Успешных</OrderMetricLabel>
              </OrderMetric>
              <OrderMetric>
                <OrderMetricValue>{failedOrders + cancelledOrders}</OrderMetricValue>
                <OrderMetricLabel>Неуспешных</OrderMetricLabel>
              </OrderMetric>
            </OrdersGrid>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Обработанный объем</StatLabel>
          </StatHeader>
          <StatContent>
            <StatValue>{formatMoney(processedVolumeRub, "RUB")}</StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#64748b",
                lineHeight: 1.4,
              }}
            >
              Прибыль: {formatMoney(netProfit, "USDT")}
              <br />
              Объем USDT: {formatMoney(processedVolumeUsdt, "USDT")}
            </div>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Процент апелляций</StatLabel>
            <StatChange positive={false}>
              Тех. апелляции / общие транзакции
            </StatChange>
          </StatHeader>
          <StatContent>
            <StatValue>
              {(statsData?.appealPercentage || 0).toFixed(1)}%
            </StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#64748b",
                lineHeight: 1.4,
              }}
            >
              Тех. апелляции: {statsData?.appealsCount || 0} | Транзакции:{" "}
              {totalOrders}
            </div>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Селектор периодов */}
      <PeriodSelector>
        <PeriodButton
          isActive={selectedPeriod === "24h"}
          onClick={() => handlePeriodChange("24h")}
        >
          24 часа
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "7d"}
          onClick={() => handlePeriodChange("7d")}
        >
          7 дней
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "30d"}
          onClick={() => handlePeriodChange("30d")}
        >
          30 дней
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "all"}
          onClick={() => handlePeriodChange("all")}
        >
          Все время
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "custom"}
          onClick={() => handlePeriodChange("custom")}
        >
          Период
        </PeriodButton>

        {/* Тайм пикер для кастомного периода */}
        <CustomPeriodContainer isVisible={selectedPeriod === "custom"}>
          <DateInput
            type="date"
            value={customDateFrom}
            onChange={(e) => handleCustomDateChange("from", e.target.value)}
            max={customDateTo || undefined}
          />
          <DateSeparator>—</DateSeparator>
          <DateInput
            type="date"
            value={customDateTo}
            onChange={(e) => handleCustomDateChange("to", e.target.value)}
            min={customDateFrom || undefined}
          />
        </CustomPeriodContainer>

        {/* Кнопка сброса - показывается только когда есть активные фильтры */}
        {shouldShowResetButton && (
          <ResetButton
            onClick={handleResetFilters}
            title="Сбросить фильтры периода"
          >
            ×
          </ResetButton>
        )}
      </PeriodSelector>

      {showWithdrawModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Вывод USDT</ModalTitle>
              <CloseButton onClick={() => setShowWithdrawModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleWithdrawSubmit}>
              <FormGroup>
                <Label>Сумма для вывода (USDT)</Label>
                <Input
                  type="number"
                  name="amount"
                  value={withdrawForm.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  max={availableBalance}
                  required
                />
                <HelperText>
                  Доступно: {formatMoney(availableBalance)}
                </HelperText>
              </FormGroup>

              <FormGroup>
                <Label>TRC-20 адрес кошелька</Label>
                <Input
                  type="text"
                  name="walletAddress"
                  value={withdrawForm.walletAddress}
                  onChange={handleInputChange}
                  placeholder="TRBLNv6JKPCdYtXoYYvKMJhcBSjnBFgFyJ"
                  pattern="^T[A-Za-z1-9]{33}$"
                  required
                />
                <HelperText>
                  Адрес должен начинаться с T и содержать 34 символа
                </HelperText>
              </FormGroup>

              {error && <ErrorText>{error}</ErrorText>}
              {success && <SuccessText>{success}</SuccessText>}

              <ModalButtonGroup>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  Отмена
                </Button>
                <Button type="submit" variant="primary" disabled={isCreating}>
                  {isCreating ? "Создание..." : "Создать заявку"}
                </Button>
              </ModalButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
}

export default Dashboard;
